import React, { useState, useRef, useEffect, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import {
  SAUDI_MAX_PENALTY_DAYS,
  capPenaltyAmount,
  dailyWageFromBasic,
} from './utils/payrollSaudiFormat';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import localApiAdapter from '../../core/services/localApiAdapter';
import { ALL_HR_DATA_REFRESH_EVENTS, useHrApiRefreshListener } from '../ressources-humaines/hrRefreshEvents';
import {
  Filter, Plus, X, FileText, Search, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle,
  ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Eye,
  LayoutGrid, Layers, Shield,
  DollarSign, Banknote, XCircle, Calendar, Info, ShieldAlert, User,
} from 'lucide-react';
import DatePicker, { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import {
  SalairesListControlsStrip,
  useClearSearchOnTableView,
  renderSalairesBulkDeleteFilterAction,
  useSalairesTableSelection,
  SALAIRES_FORM_GRID_CLASS,
  useSalairesEmployeeOptions,
  QawaemTableStickyActionsTd,
} from './salairesListPageUtils';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import '../../core/styles/global-dropdowns.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('ar', ar);
registerLocale('ar-SA', ar);

/** Turn API / Error values into a string safe for alert() — never [object Object] */
function toUserMessage(value, fallback) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (typeof value.message === 'string') return value.message;
    if (typeof value.error === 'string') return value.error;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

const IncidentsPrejudicesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.incidentsPrejudicesPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);

  const formatAmountDisplay = (val) => {
    if (val == null || val === '') return p('not_available');
    const n = parseFloat(val);
    if (Number.isNaN(n)) return p('not_available');
    return formatBrandMoney(n);
  };

  const fmtDate = (val) => (val ? new Date(val).toLocaleDateString(dateLocale) : p('not_available'));
  const { isDashboardLightMode } = useGlobalTheme();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const unwrapApiList = (response) => {
    if (Array.isArray(response)) return response;
    if (response?.success && Array.isArray(response.data)) return response.data;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  };

  const INCIDENT_TYPE_OPTIONS = [
    { value: 'work_accident', label: p('form_work_accident') },
    { value: 'policy_violation', label: p('form_policy_violation') },
    { value: 'security_incident', label: p('form_security_incident') },
    { value: 'other', label: p('form_other') },
  ];
  const DOMMAGE_TYPE_OPTIONS = [
    { value: 'material', label: p('form_material') },
    { value: 'vehicle', label: p('form_vehicle') },
    { value: 'infrastructure', label: p('form_infrastructure') },
    { value: 'other', label: p('form_other') },
  ];
  const GRAVITE_OPTIONS = [
    { value: 'low', label: p('severity_low') },
    { value: 'medium', label: p('severity_medium') },
    { value: 'high', label: p('severity_high') },
    { value: 'critical', label: p('severity_critical') },
  ];
  const STATUT_INCAP_OPTIONS = [
    { value: 'none', label: p('incapacity_none') },
    { value: 'temporary', label: p('incapacity_temporary') },
    { value: 'permanent', label: p('incapacity_permanent') },
  ];
  const [activeTab, setActiveTab] = useState('incidents');
  const entityLabel = () => (activeTab === 'incidents'
    ? p('tab_incidents', { defaultValue: 'حادث' })
    : p('tab_damages', { defaultValue: 'ضرر' }));
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useClearSearchOnTableView(viewMode, searchTerm, setSearchTerm);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_items');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    editingKey: columnEditingKey,
    setEditingKey: setColumnEditingKey,
  } = useColumnHeaderFilters();
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  // Form data
  const [formData, setFormData] = useState({
    employeeId: '',
    date: '',
    type: '',
    description: '',
    gravite: '',
    statutIncapacite: '',
    montantEstime: '',
    montantRetenu: '',
    impactSalaire: false,
    commentaire: ''
  });
  
  // Import/export dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Refs for dropdowns
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showModal && !showDetailsModal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showModal, showDetailsModal]);
  
  // Données réelles depuis l'API
  const [incidents, setIncidents] = useState([]);
  const [prejudices, setPrejudices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { employees } = useSalairesEmployeeOptions();

  const employeeSelectOptions = useMemo(
    () => employees
      .filter((emp) => emp.mat && emp.prenom && emp.nom)
      .map((emp) => ({
        value: emp.mat,
        label: `${emp.prenom} ${emp.nom} (${emp.mat})`,
      })),
    [employees],
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      const [incidentsRes, damagesRes] = await Promise.all([
        localApiAdapter.get('/salaries/incidents-damages/incidents'),
        localApiAdapter.get('/salaries/incidents-damages/damages'),
      ]);

      setIncidents(sortRecordsNewestFirst(unwrapApiList(incidentsRes)));
      setPrejudices(sortRecordsNewestFirst(unwrapApiList(damagesRes)));
    } catch (error) {
      console.error('Error fetching incidents/damages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useHrApiRefreshListener(ALL_HR_DATA_REFRESH_EVENTS, fetchData);

  // Types d'incidents
  const incidentTypes = [
    { id: 'repeated_lateness', label: p('type_repeated_lateness') },
    { id: 'unexcused_absence', label: p('type_unexcused_absence') },
    { id: 'policy_violation', label: p('type_policy_violation') },
    { id: 'disciplinary', label: p('type_disciplinary') },
    { id: 'work_accident', label: p('type_accident') },
  ];
  
  const prejudiceTypes = [
    { id: 'material', label: p('type_material_damage') },
    { id: 'vehicle', label: p('type_vehicle_damage') },
    { id: 'equipment', label: p('type_lost_tooling') },
    { id: 'cash_shortage', label: p('type_cash_shortage') },
    { id: 'client_penalty', label: p('type_client_penalty') },
    { id: 'other', label: p('type_other_financial') },
  ];
  
  // États des incidents (valeurs alignées sur la colonne SQLite `status`)
  const incidentStatus = [
    { id: 'open', label: p('status_open'), color: 'bg-yellow-500/20 text-yellow-400' },
    { id: 'in_progress', label: p('status_in_progress_long'), color: 'bg-blue-500/20 text-blue-400' },
    { id: 'resolved', label: p('status_resolved'), color: 'bg-green-500/20 text-green-400' },
    { id: 'closed', label: p('status_closed'), color: 'bg-gray-500/20 text-gray-400' },
  ];
  
  // États des dommages (valeurs alignées sur la colonne SQLite `status`)
  const prejudiceStatus = [
    { id: 'not_processed', label: p('status_not_processed'), color: 'bg-yellow-500/20 text-yellow-400' },
    { id: 'evaluation_in_progress', label: p('status_evaluation'), color: 'bg-blue-500/20 text-blue-400' },
    { id: 'deduction_planned', label: p('status_deduction_planned'), color: 'bg-purple-500/20 text-purple-400' },
    { id: 'deduction_applied', label: p('status_deduction_applied'), color: 'bg-green-500/20 text-green-400' },
    { id: 'cancelled', label: p('status_cancelled'), color: 'bg-gray-500/20 text-gray-400' },
  ];
  
  // Récupérer les données de l'employé par ID
  // Note: API data already includes employee_name and employee_mat, so this is simplified
  const getEmployeeById = (id) => {
    // This is kept for compatibility but might not be needed
    return { nom: '', prenom: '', mat: '' };
  };
  
  // Récupérer les informations d'un type
  const getTypeInfo = (id, isIncident = true) => {
    const types = isIncident ? incidentTypes : prejudiceTypes;
    const opts = isIncident ? INCIDENT_TYPE_OPTIONS : DOMMAGE_TYPE_OPTIONS;
    const hit = types.find((type) => type.id === id || type.label === id)
      || opts.find((o) => o.value === id);
    return hit
      ? { id: hit.id || hit.value, label: hit.label }
      : { id: id || '', label: id || '—' };
  };
  
  // Récupérer les informations d'un statut
  const getStatusInfo = (id, isIncident = true) => {
    const statuses = isIncident ? incidentStatus : prejudiceStatus;
    return (
      statuses.find((status) => status.id === id) || {
        id: id || '',
        label: id || '—',
        color: 'bg-gray-500/20 text-gray-400'
      }
    );
  };
  
  // Filtrer les éléments
  const currentItems = activeTab === 'incidents' ? incidents : prejudices;
  const isIncidentTab = activeTab === 'incidents';

  const incidentStatusOptions = [
    { value: 'open', label: p('status_open'), icon: AlertTriangle, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'in_progress', label: p('status_in_progress'), icon: RefreshCcw, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'resolved', label: p('status_resolved'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'closed', label: p('status_closed'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  ];

  const prejudiceStatusOptions = [
    { value: 'not_processed', label: p('status_not_processed'), icon: AlertTriangle, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'evaluation_in_progress', label: p('status_evaluation'), icon: RefreshCcw, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'deduction_planned', label: p('status_deduction_planned'), icon: Shield, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { value: 'deduction_applied', label: p('status_deduction_applied'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  ];

  const statusOptions = isIncidentTab ? incidentStatusOptions : prejudiceStatusOptions;

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const endpoint = isIncidentTab ? '/salaries/incidents-damages/incidents' : '/salaries/incidents-damages/damages';
      const item = currentItems.find((i) => i.id === recordId);
      if (!item) return;
      await localApiAdapter.put(`${endpoint}/${recordId}`, { ...item, status: newStatus });
      await fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const columnFilterRules = useMemo(() => {
    const rules = [
      textColumnRule('employee_name', (row) => `${row.employee_name || ''} ${row.employee_mat || ''}`.trim()),
      textColumnRule('date', (row) => fmtDate(row.date)),
      textColumnRule('type', (row) => row.type || ''),
      exactColumnRule('status', (row) => row.status || ''),
    ];
    if (!isIncidentTab) {
      rules.splice(3, 0, textColumnRule('montant', (row) => String(row.estimated_amount ?? '')));
    }
    return rules;
  }, [isIncidentTab, dateLocale]);

  const colHeaderFilterProps = (key, label, extra = {}) => ({
    columnKey: key,
    label,
    value: columnFilters[key] ?? '',
    onChange: (v) => setColumnFilter(key, v),
    editingKey: columnEditingKey,
    onEditingKeyChange: setColumnEditingKey,
    thClassName: 'text-white font-semibold',
    ...extra,
  });

  const mainTabs = [
    { id: 'all_items', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
    { id: 'par_type', name: p('tab_by_type'), icon: Layers },
    { id: 'par_impact', name: p('tab_by_impact'), icon: DollarSign },
  ];

  const quickFilterButtons = isIncidentTab
    ? [
        { id: 'all', name: p('filter_all'), icon: Layers },
        { id: 'open', name: p('status_open_plural'), icon: AlertTriangle },
        { id: 'in_progress', name: p('status_in_progress'), icon: RefreshCcw },
        { id: 'resolved', name: p('status_resolved_plural'), icon: CheckCircle },
      ]
    : [
        { id: 'all', name: p('filter_all'), icon: Layers },
        { id: 'not_processed', name: p('status_not_processed_plural'), icon: AlertTriangle },
        { id: 'evaluation_in_progress', name: p('status_evaluation'), icon: RefreshCcw },
        { id: 'deduction_applied', name: p('status_deduction_applied_plural'), icon: CheckCircle },
      ];

  const getTypeDistribution = () => {
    const types = isIncidentTab ? incidentTypes : prejudiceTypes;
    return types.map((typeItem, i) => ({
      filterKey: typeItem.id,
      name: typeItem.label,
      count: currentItems.filter((item) => item.type === typeItem.id || item.type === typeItem.label).length,
      color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-green-500 to-green-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-orange-500 to-orange-600', 'bg-gradient-to-r from-yellow-500 to-yellow-600', 'bg-gradient-to-r from-red-500 to-red-600'][i % 6],
    }));
  };

  const itemDistribution = {
    all_items: {
      title: isIncidentTab ? p('overview_incidents') : p('overview_damages'),
      data: isIncidentTab
        ? [
            { filterKey: 'open', name: p('status_open'), count: incidents.filter((i) => i.status === 'open').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
            { filterKey: 'in_progress', name: p('status_in_progress'), count: incidents.filter((i) => i.status === 'in_progress').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
            { filterKey: 'resolved', name: p('status_resolved'), count: incidents.filter((i) => i.status === 'resolved').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
            { filterKey: 'closed', name: p('status_closed'), count: incidents.filter((i) => i.status === 'closed').length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
          ]
        : [
            { filterKey: 'not_processed', name: p('status_not_processed'), count: prejudices.filter((pr) => pr.status === 'not_processed').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
            { filterKey: 'evaluation_in_progress', name: p('status_evaluation'), count: prejudices.filter((pr) => pr.status === 'evaluation_in_progress').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
            { filterKey: 'deduction_planned', name: p('status_deduction_planned'), count: prejudices.filter((pr) => pr.status === 'deduction_planned').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
            { filterKey: 'deduction_applied', name: p('status_deduction_applied'), count: prejudices.filter((pr) => pr.status === 'deduction_applied').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
          ],
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: [],
    },
    par_type: {
      title: p('distribution_by_type'),
      data: getTypeDistribution(),
    },
    par_impact: {
      title: p('distribution_by_impact'),
      data: [
        { filterKey: 'with_impact', name: p('impact_salary'), count: currentItems.filter((i) => i.salary_impact === 1).length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'no_impact', name: p('no_impact'), count: currentItems.filter((i) => i.salary_impact !== 1).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
  };

  itemDistribution.par_statut.data = itemDistribution.all_items.data;

  const distributionInfo = itemDistribution[mainViewMode] || itemDistribution.all_items;

  const filteredItems = useMemo(() => {
    const base = currentItems.filter((item) => {
      const searchLower = searchTerm.toLowerCase();
      if (searchTerm && !item.employee_name?.toLowerCase().includes(searchLower) && !item.employee_mat?.toLowerCase().includes(searchLower) && !item.description?.toLowerCase().includes(searchLower)) {
        return false;
      }
      if (mainViewMode === 'all_items' && quickFilter !== 'all' && item.status !== quickFilter) return false;
      if (activeDistributionFilter) {
        if (mainViewMode === 'all_items' || mainViewMode === 'par_statut') {
          if (item.status !== activeDistributionFilter) return false;
        }
        if (mainViewMode === 'par_impact') {
          if (activeDistributionFilter === 'with_impact' && item.salary_impact !== 1) return false;
          if (activeDistributionFilter === 'no_impact' && item.salary_impact === 1) return false;
        }
        if (mainViewMode === 'par_type') {
          const types = isIncidentTab ? incidentTypes : prejudiceTypes;
          const typeDef = types.find((typeItem) => typeItem.id === activeDistributionFilter);
          if (typeDef && item.type !== typeDef.id && item.type !== typeDef.label) return false;
        }
      }
      return true;
    });

    const filtered = applyColumnFilters(base, columnFilters, columnFilterRules);
    return [...filtered].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [currentItems, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, isIncidentTab, columnFilters, columnFilterRules, incidentTypes, prejudiceTypes]);

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useSalairesTableSelection(filteredItems);
  
  const handleViewDetails = (item) => {
    setDetailItem(item);
    setShowDetailsModal(true);
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm(t('common.delete_confirmation'))) return;

    try {
      const endpoint = activeTab === 'incidents'
        ? '/salaries/incidents-damages/incidents'
        : '/salaries/incidents-damages/damages';
      await localApiAdapter.delete(`${endpoint}/${id}`);
      notifyDeleted(showNotify, entityLabel());
      await fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      notifyError(showNotify, toUserMessage(error?.message || error, p('save_error')));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRecords.length) return;
    if (!window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) return;
    try {
      const endpoint = activeTab === 'incidents'
        ? '/salaries/incidents-damages/incidents'
        : '/salaries/incidents-damages/damages';
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((recordId) => localApiAdapter.delete(`${endpoint}/${recordId}`)));
      clearSelection();
      notifyBulkDeleted(showNotify, deleteCount, entityLabel());
      await fetchData();
    } catch (error) {
      console.error('Error bulk deleting:', error);
      notifyError(showNotify, p('save_error'));
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingRecord(null);
    setFormData({
      employeeId: '',
      date: '',
      type: '',
      description: '',
      gravite: '',
      statutIncapacite: '',
      montantEstime: '',
      montantRetenu: '',
      impactSalaire: false,
      commentaire: ''
    });
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    setFormData({
      employeeId: '',
      date: '',
      type: '',
      description: '',
      gravite: '',
      statutIncapacite: '',
      montantEstime: '',
      montantRetenu: '',
      impactSalaire: false,
      commentaire: ''
    });
    setShowModal(true);
  };
  
  const handleEdit = (item) => {
    setEditingRecord(item);
    setFormData({
      employeeId: item.employee_mat || item.employee_id || '',
      date: item.date || '',
      type: item.type || '',
      description: item.description || '',
      gravite: item.severity || '',
      statutIncapacite: item.disability_status || '',
      montantEstime: item.estimated_amount || '',
      montantRetenu: item.deducted_amount || '',
      impactSalaire: item.salary_impact === 1,
      commentaire: item.notes || ''
    });
    setShowModal(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employeeId?.trim() || !formData.date || !formData.type?.trim() || !formData.description?.trim()) {
      notifyError(showNotify, p('validation_required_fields'));
      return;
    }
    try {
      const isIncident = activeTab === 'incidents';
      const endpoint = isIncident
        ? '/salaries/incidents-damages/incidents'
        : '/salaries/incidents-damages/damages';

      const payload = isIncident ? {
        employee_id: formData.employeeId,
        date: formData.date,
        type: formData.type,
        description: formData.description,
        severity: formData.gravite,
        disability_status: formData.statutIncapacite,
        salary_impact: formData.impactSalaire ? 1 : 0,
        notes: formData.commentaire,
        status: editingRecord?.status || 'open'
      } : {
        employee_id: formData.employeeId,
        date: formData.date,
        type: formData.type,
        description: formData.description,
        estimated_amount: parseFloat(formData.montantEstime) || 0,
        deducted_amount: (() => {
          const emp = employees.find((e) => e.mat === formData.employeeId);
          const daily = dailyWageFromBasic(emp?.salaireBase ?? emp?.salary ?? 0);
          const requested = parseFloat(formData.montantRetenu) || 0;
          const maxAllowed = capPenaltyAmount(daily, SAUDI_MAX_PENALTY_DAYS);
          return Math.min(requested, maxAllowed);
        })(),
        salary_impact: formData.impactSalaire ? 1 : 0,
        notes: formData.commentaire,
        status: editingRecord?.status || 'not_processed'
      };

      if (editingRecord) {
        await localApiAdapter.put(`${endpoint}/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel());
      } else {
        await localApiAdapter.post(endpoint, payload);
        notifyCreated(showNotify, entityLabel());
      }
      await fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, toUserMessage(error?.message || error, p('network_error')));
    }
  };

  // Calculate GLOBAL stats (combined for both tabs)
  const statsGlobal = {
    totalIncidents: incidents.length,
    totalDommages: prejudices.length,
    totalEnCours:
      incidents.filter((i) => ['open', 'in_progress'].includes(i.status)).length +
      prejudices.filter((p) => ['not_processed', 'evaluation_in_progress'].includes(p.status)).length,
    totalResolus:
      incidents.filter((i) => ['resolved', 'closed'].includes(i.status)).length +
      prejudices.filter((p) => ['deduction_planned', 'deduction_applied'].includes(p.status)).length,
    montantEstimeTotal: prejudices.reduce((sum, p) => sum + (Number(p.estimated_amount) || 0), 0),
    montantRetenuTotal: prejudices.reduce((sum, p) => sum + (Number(p.deducted_amount) || 0), 0)
  };

  const qawaemBaseColumns = useMemo(() => {
    const cols = [
      { key: 'employee_name', label: p('col_employee'), required: true },
      { key: 'date', label: p('col_date') },
      { key: 'type', label: p('col_type') },
      { key: 'description', label: p('col_description') },
    ];
    if (activeTab === 'dommages') {
      cols.push({ key: 'montant', label: p('col_amount') });
    }
    cols.push({ key: 'status', label: p('col_status') });
    return ensureStatusBeforeActions(cols);
  }, [activeTab, p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, t('common.actions')),
    [pickerColumns, t],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol),
    [qawaemBaseColumns, showCol],
  );
  
  return (
    <div className="space-y-6">
      {/* Header with Ajouter button */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FileText className="h-8 w-8 text-blue-400" />
            {activeTab === 'incidents' ? p('title_incidents') : p('title_damages')}
          </h1>
          <p className="text-white/60 mt-2">
            {activeTab === 'incidents' ? p('subtitle_incidents') : p('subtitle_damages')}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{t('common.advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={openCreateModal}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
          >
            <Plus size={20} />
            <span>{activeTab === 'incidents' ? p('add_incident') : p('add_damage')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('kpi_total_incidents'), value: statsGlobal.totalIncidents, icon: AlertTriangle, color: 'from-blue-500 to-blue-600' },
          { title: p('kpi_total_damages'), value: statsGlobal.totalDommages, icon: ShieldAlert, color: 'from-purple-500 to-purple-600' },
          { title: p('kpi_in_progress'), value: statsGlobal.totalEnCours, icon: RefreshCcw, color: 'from-orange-500 to-orange-600' },
          { title: p('kpi_resolved'), value: statsGlobal.totalResolus, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('kpi_amounts'), value: `${formatAmountDisplay(statsGlobal.montantEstimeTotal)} / ${formatAmountDisplay(statsGlobal.montantRetenuTotal)}`, icon: DollarSign, color: 'from-yellow-500 to-yellow-600' },
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? 'auto' : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }} style={{ overflow: 'hidden' }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_items' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} type="button" onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"><XCircle size={14} />{p('reset_filter')}</button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }} />
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Tabs Navigation - MOVED BELOW CARDS */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1">
          <button
            onClick={() => setActiveTab('incidents')}
            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
              activeTab === 'incidents'
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <AlertTriangle className="h-5 w-5" />
            <span>{p('tab_incidents')}</span>
          </button>
          
          <button
            onClick={() => setActiveTab('dommages')}
            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
              activeTab === 'dommages'
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <ShieldAlert className="h-5 w-5" />
            <span>{p('tab_damages')}</span>
          </button>
        </div>
      </div>

      {/* Data Table with Integrated Header */}
      <div className="glass-panel glass-table-shell overflow-visible">
        <SalairesListControlsStrip
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
          isDashboardLightMode={isDashboardLightMode}
          cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
          tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
          filterActions={renderSalairesBulkDeleteFilterAction({
            selectedCount: selectedRecords.length,
            onBulkDelete: handleBulkDelete,
            t,
          })}
        />
        {viewMode === 'table' ? (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('employee_name') ? <TableColumnHeaderFilter {...colHeaderFilterProps('employee_name', p('col_employee'))} /> : null}
                  {showCol('date') ? <TableColumnHeaderFilter {...colHeaderFilterProps('date', p('col_date'))} /> : null}
                  {showCol('type') ? <TableColumnHeaderFilter {...colHeaderFilterProps('type', p('col_type'))} /> : null}
                  {showCol('description') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_description')}</th> : null}
                  {activeTab === 'dommages' && showCol('montant') ? (
                    <TableColumnHeaderFilter {...colHeaderFilterProps('montant', p('col_amount'))} />
                  ) : null}
                  {showCol('status') ? (
                  <TableColumnHeaderFilter
                    {...colHeaderFilterProps('status', p('col_status'), {
                      align: 'center',
                      kind: 'select',
                      options: statusOptions.map((o) => ({ value: o.value, label: o.label })),
                    })}
                  />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={visibleTableColCount} className="text-center py-12">
                      <Info className="h-12 w-12 text-white/30 mx-auto mb-3" />
                      <p className="text-white/50 text-lg">{activeTab === 'incidents' ? p('empty_incident') : p('empty_damage')}</p>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item, index) => {
                  const typeInfo = getTypeInfo(item.type, activeTab === 'incidents');
                  const statusInfo = getStatusInfo(item.status, activeTab === 'incidents');
                  
                  return (
                      <tr
                        key={item.id}
                        className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(item.id) ? 'bg-white/10' : ''}`}
                      >
                        <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(item.id)}
                              onChange={() => handleSelectRecord(item.id)}
                            />
                          </div>
                        </td>
                        {showCol('employee_name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{item.employee_name}</span>
                            <span className="text-white/50 text-xs mt-0.5">{item.employee_mat}</span>
                          </div>
                        </td>
                        ) : null}
                        {showCol('date') ? (
                        <td className="px-6 py-4">
                          <span className="text-white/70 text-sm">{fmtDate(item.date)}</span>
                        </td>
                        ) : null}
                        {showCol('type') ? (
                        <td className="px-6 py-4">
                          <span className="text-white/70 text-sm">{typeInfo.label}</span>
                        </td>
                        ) : null}
                        {showCol('description') ? (
                        <td className="px-6 py-4">
                          <span className="text-white/70 text-sm max-w-xs truncate block">{item.description}</span>
                        </td>
                        ) : null}
                        {activeTab === 'dommages' && showCol('montant') && (
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="text-white font-bold text-lg">{formatAmountDisplay(item.estimated_amount || 0)}</span>
                              {item.deducted_amount > 0 && (
                                <span className="text-red-400 text-xs mt-0.5">{p('deducted_short')} {formatAmountDisplay(item.deducted_amount || 0)}</span>
                              )}
                            </div>
                          </td>
                        )}
                        {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={item.status}
                              recordId={item.id}
                              options={statusOptions}
                              onChange={handleStatusChange}
                              title={p('change_status_title', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                            />
                          </div>
                        </td>
                        ) : null}
                        <QawaemTableStickyActionsTd>
                          <QawaemErpRowActions
                            onView={() => handleViewDetails(item)}
                            onEdit={() => handleEdit(item)}
                            onDelete={() => handleDelete(item.id)}
                            viewTitle={t('common.view_details')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </QawaemTableStickyActionsTd>
                      </tr>
                  );
                  })
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{activeTab === 'incidents' ? p('footer_total_incidents', { count: filteredItems.length }) : p('footer_total_damages', { count: filteredItems.length })}</span>
                      {activeTab === 'dommages' && (
                        <>
                          <span>{p('footer_estimated_total')} <span className="text-white font-semibold">{formatAmountDisplay(filteredItems.reduce((sum, item) => sum + (parseFloat(item.estimated_amount) || 0), 0))}</span></span>
                          <span>{p('footer_deducted_total')} <span className="text-white font-semibold">{formatAmountDisplay(filteredItems.reduce((sum, item) => sum + (parseFloat(item.deducted_amount) || 0), 0))}</span></span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => {
                const typeInfo = getTypeInfo(item.type, activeTab === 'incidents');
                const statusInfo = getStatusInfo(item.status, activeTab === 'incidents');
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
                          <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            {activeTab === 'incidents' ? <AlertTriangle size={20} className="text-orange-400" /> : <ShieldAlert size={20} className="text-purple-400" />}
                            {item.employee_name}
                          </h3>
                          <QawaemClickableStatusBadge
                            status={item.status}
                            recordId={item.id}
                            options={statusOptions}
                            onChange={handleStatusChange}
                          />
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_employee')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div><span className="font-medium">{p('label_matricule')}</span> {item.employee_mat}</div>
                              <div><span className="font-medium">{p('label_date')}</span> {fmtDate(item.date)}</div>
                            </div>
                          </div>
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_type_severity')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div><span className="font-medium">{p('label_type')}</span> {typeInfo.label}</div>
                              {item.severity && <div><span className="font-medium">{p('label_severity')}</span> {item.severity}</div>}
                            </div>
                          </div>
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_description')}</h5>
                            <p className="text-sm text-white/70 line-clamp-3">{item.description}</p>
                          </div>
                          <div>
                            <h5 className="font-medium text-white mb-2">{activeTab === 'dommages' ? p('card_amounts') : p('card_impact')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              {activeTab === 'dommages' ? (
                                <>
                                  <div><span className="font-medium">{p('label_estimated')}</span> <span className="text-white font-bold">{formatAmountDisplay((item.estimated_amount || 0))}</span></div>
                                  {(item.deducted_amount || 0) > 0 && <div><span className="font-medium text-red-400">{p('label_deducted')}</span> {formatAmountDisplay((item.deducted_amount || 0))}</div>}
                                </>
                              ) : (
                                <div><span className="font-medium">{p('label_salary_impact')}</span> {item.salary_impact === 1 ? p('yes') : p('no')}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleViewDetails(item)}
                        onEdit={() => handleEdit(item)}
                        onDelete={() => handleDelete(item.id)}
                        viewTitle={t('common.view_details')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <div className="text-center py-12">
                <Info className="h-12 w-12 text-white/30 mx-auto mb-3" />
                <p className="text-white/50 text-lg">{activeTab === 'incidents' ? p('empty_incident') : p('empty_damage')}</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {showDetailsModal && detailItem ? (
      <QawaemErpViewModal
        open={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); setDetailItem(null); }}
        title={p('details_title')}
        icon={Eye}
        testId="incidents-view-modal"
      >
          <div className="space-y-4 text-white">
            <div>
              <p className="text-white/60 text-sm">{p('col_employee')}</p>
              <p className="font-semibold">{detailItem.employee_name}</p>
            </div>
            <div>
              <p className="text-white/60 text-sm">{p('col_description')}</p>
              <p>{detailItem.description}</p>
            </div>
            <div>
              <p className="text-white/60 text-sm">{p('col_date')}</p>
              <p>{fmtDate(detailItem.date)}</p>
            </div>
            {detailItem.notes ? (
              <div>
                <p className="text-white/60 text-sm">{p('label_comment')}</p>
                <p>{detailItem.notes}</p>
              </div>
            ) : null}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => { setShowDetailsModal(false); setDetailItem(null); }}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 rounded-xl text-white transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
      </QawaemErpViewModal>
      ) : null}

      {/* Create/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? (activeTab === 'incidents' ? p('modal_edit_incident') : p('modal_edit_damage')) : (activeTab === 'incidents' ? p('modal_new_incident') : p('modal_new_damage'))}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="incident-dommage-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('update') : p('create')}</button>
                <button type="button" onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="incident-dommage-form" onSubmit={handleSubmit}>
                <div className={SALAIRES_FORM_GRID_CLASS}>
                  <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('employee_required')} required /></label><CustomSelect value={formData.employeeId} onChange={(value) => setFormData({ ...formData, employeeId: value })} options={employeeSelectOptions} placeholder={p('employee_placeholder')} autoSelect={false} searchable creatable /></div>
                  <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('date_required')} required /></label><CustomDatePicker value={formData.date} onChange={(v) => setFormData({ ...formData, date: v })} className="w-full" /></div>
                  <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('type_required')} required /></label><CustomSelect value={formData.type} onChange={(value) => setFormData({ ...formData, type: value })} options={activeTab === 'incidents' ? INCIDENT_TYPE_OPTIONS : DOMMAGE_TYPE_OPTIONS} placeholder={p('type_placeholder')} autoSelect={false} searchable creatable /></div>
                  {activeTab === 'incidents' ? (
                    <>
                      <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('severity_label')}</label><CustomSelect value={formData.gravite} onChange={(value) => setFormData({ ...formData, gravite: value })} options={GRAVITE_OPTIONS} placeholder={p('severity_placeholder')} autoSelect={false} searchable creatable /></div>
                      <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('incapacity_status_label')}</label><CustomSelect value={formData.statutIncapacite} onChange={(value) => setFormData({ ...formData, statutIncapacite: value })} options={STATUT_INCAP_OPTIONS} placeholder={p('incapacity_status_placeholder')} autoSelect={false} searchable creatable /></div>
                    </>
                  ) : (
                    <>
                      <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('estimated_amount_label')}</label><CustomNumberInput step={0.01} decimals={2} value={formData.montantEstime} onChange={(v) => setFormData({ ...formData, montantEstime: v })} placeholder="15000" className="w-full" /></div>
                      <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('deducted_amount_label')}</label><CustomNumberInput step={0.01} decimals={2} value={formData.montantRetenu} onChange={(v) => setFormData({ ...formData, montantRetenu: v })} placeholder="500" className="w-full" /><p className="text-white/40 text-xs mt-1">{p('penalty_max_hint', { days: SAUDI_MAX_PENALTY_DAYS })}</p></div>
                    </>
                  )}
                  <div className="min-w-0 flex items-center gap-3"><input type="checkbox" id="impactSalaire" checked={formData.impactSalaire} onChange={(e) => setFormData({ ...formData, impactSalaire: e.target.checked })} className="w-5 h-5 bg-white/10 border border-white/20 rounded focus:ring-2 focus:ring-blue-500" /><label htmlFor="impactSalaire" className="text-white/70 text-sm font-medium cursor-pointer">{p('salary_impact_checkbox')}</label></div>
                  <div className="md:col-span-3 min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('description_required')} required /></label><textarea required value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" rows="2" placeholder={p('description_placeholder')} /></div>
                  <div className="md:col-span-3 min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_comment')} <span className="text-white/40 text-xs">{p('comment_optional')}</span></label><textarea value={formData.commentaire} onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" rows="3" placeholder={p('comment_placeholder')} /></div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default IncidentsPrejudicesPage; 


